import React, { useState, FormEvent, ChangeEvent } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { authService } from 'services/auth/authService';
import { toast } from 'sonner';
import Spinner from 'components/common/Spinner';
import { ReactComponent as Logomark } from 'assets/images/logos/Logomark.svg';
import { AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'contexts/ThemeContext';
import { getErrorMessage } from 'utils/errors';

interface LocationState {
  email: string;
}

interface ErrorMessages {
  otp?: string;
  newPassword?: string;
  confirmPassword?: string;
  general?: string;
}

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const [otp, setOtp] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [errorMessages, setErrorMessages] = useState<ErrorMessages>({});
  const [localLoading, setLocalLoading] = useState<boolean>(false);
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { email } = (location.state as LocationState) || {};

  const validateForm = (): boolean => {
    const errors: ErrorMessages = {};
    const otpRegex = /^\d{6}$/;
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

    if (!otp) {
      errors.otp = t('auth.resetPassword.validation.otpRequired');
    } else if (!otpRegex.test(otp)) {
      errors.otp = t('auth.resetPassword.validation.otpInvalid');
    }

    if (!newPassword) {
      errors.newPassword = t('auth.resetPassword.validation.newPasswordRequired');
    } else if (!passwordRegex.test(newPassword)) {
      errors.newPassword = t('auth.resetPassword.validation.passwordRequirements');
    }

    if (!confirmPassword) {
      errors.confirmPassword = t('auth.resetPassword.validation.confirmPasswordRequired');
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = t('auth.resetPassword.validation.passwordsNotMatch');
    }

    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResetPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLocalLoading(true);
      const response: AxiosResponse = await authService.resetPassword(email, otp, newPassword);

      if (response.status === 200) {
        toast.success(t('auth.resetPassword.successMessage'));
        navigate('/login');
      } else {
        throw new Error(response.data.message || t('auth.resetPassword.resetFailed'));
      }
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, t('auth.resetPassword.resetFailedTryAgain'));
      setErrorMessages({ general: errorMessage });
      toast.error(errorMessage);
    } finally {
      setLocalLoading(false);
    }
  };

  const inputClassName = `w-full px-3 py-2.5 text-base leading-6 rounded-fc outline-none transition-all ${
    isDark
      ? 'bg-fc-dark-surface-input text-fc-dark-text-primary placeholder-fc-dark-text-muted fc-input-gradient-border-dark'
      : 'bg-white text-fc-text-primary placeholder-fc-text-muted fc-input-gradient-border'
  }`;

  const inputStyle = { '--fc-input-bg': isDark ? '#0F172B' : '#FFFFFF' } as React.CSSProperties;

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 font-geist transition-colors duration-300 ${
        isDark ? 'bg-fc-dark' : 'bg-fc-light'
      }`}
    >
      {localLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 ${isDark ? 'bg-[#0F172B]' : 'bg-white'}`}>
            <Spinner size="large" text={t('auth.resetPassword.resettingPassword')} />
          </div>
        </div>
      )}

      <div className="w-full max-w-[480px] flex flex-col items-center gap-6">
        {/* Logo */}
        <Logomark className="w-20 h-20" />

        {/* Title + Description */}
        <div className="w-full text-center space-y-4">
          <h1
            className={`text-2xl font-bold leading-8 ${
              isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
            }`}
          >
            {t('auth.resetPassword.title')}
          </h1>
          <p
            className={`text-base font-normal leading-6 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.resetPassword.subtitle', { email })}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleResetPassword} className="w-full space-y-4">
          {/* OTP field */}
          <div className="space-y-1">
            <label
              htmlFor="otp"
              className={`block text-base font-medium leading-6 ${
                isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
              }`}
            >
              {t('auth.resetPassword.otpLabel')}
            </label>
            <input
              type="text"
              id="otp"
              placeholder={t('auth.resetPassword.otpPlaceholder')}
              value={otp}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setOtp(e.target.value)}
              required
              inputMode="numeric"
              maxLength={6}
              autoComplete="one-time-code"
              style={inputStyle}
              className={inputClassName}
            />
            {errorMessages.otp && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errorMessages.otp}</p>
            )}
          </div>

          {/* New Password field */}
          <div className="space-y-1">
            <label
              htmlFor="newPassword"
              className={`block text-base font-medium leading-6 ${
                isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
              }`}
            >
              {t('auth.resetPassword.newPasswordLabel')}
            </label>
            <input
              type="password"
              id="newPassword"
              placeholder={t('auth.resetPassword.newPasswordPlaceholder')}
              value={newPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={inputStyle}
              className={inputClassName}
            />
            {errorMessages.newPassword && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errorMessages.newPassword}</p>
            )}
          </div>

          {/* Confirm Password field */}
          <div className="space-y-1">
            <label
              htmlFor="confirmPassword"
              className={`block text-base font-medium leading-6 ${
                isDark ? 'text-fc-dark-text-primary' : 'text-fc-text-primary'
              }`}
            >
              {t('auth.resetPassword.confirmPasswordLabel')}
            </label>
            <input
              type="password"
              id="confirmPassword"
              placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
              value={confirmPassword}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              style={inputStyle}
              className={inputClassName}
            />
            {errorMessages.confirmPassword && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{errorMessages.confirmPassword}</p>
            )}
          </div>

          {/* Error message */}
          {errorMessages.general && (
            <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-3 rounded">
              <p className="text-sm text-red-700 dark:text-red-400">{errorMessages.general}</p>
            </div>
          )}

          {/* Submit Button - Gradient */}
          <button
            type="submit"
            disabled={localLoading}
            className="w-full h-12 bg-fc-gradient-btn text-white text-base font-medium leading-6 rounded-fc-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {localLoading ? (
              <>
                <Spinner size="small" color="white" />
                <span>{t('auth.resetPassword.resetting')}</span>
              </>
            ) : (
              t('auth.resetPassword.resetButton')
            )}
          </button>
        </form>

        {/* Back to Login Link */}
        <p className="text-center">
          <span
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-fc-dark-text-secondary' : 'text-fc-text-secondary'
            }`}
          >
            {t('auth.resetPassword.backTo')}{' '}
          </span>
          <Link
            to="/login"
            className={`text-xs font-medium leading-4 ${
              isDark ? 'text-[#51A2FF]' : 'text-fc-blue'
            } hover:underline`}
          >
            {t('auth.resetPassword.loginLink')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
